import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import ActiveJobListRow from './ActiveJobListRow'

/**
 * List of active jobs with vertical scroll.
 *
 * @param {Object} props
 * @param {Object[]} props.items  list of active jobs
 * @param {Object|null} props.selectedFabricationOrder selected fabrication order
 * @param {Object|null} props.selectedJobReference selected job
 * @param {Object} props.progressByKey map id->percentage
 * @param {function(Object): void} props.onSelectNode select job
 * @param {Object} [props.batchBadges] S239 (enmienda 06): mapa `batchId -> {color, label}` con el
 *   distintivo PERMANENTE de cada lote presente entre los jobs activos. Un job sin lote no encuentra
 *   entrada aquí y, por eso, no lleva distintivo.
 */
const ActiveJobList = ({
  items,
  selectedFabricationOrder,
  selectedJobReference,
  progressByKey,
  onSelectNode,
  batchBadges = {}
}) => {
  const selectedKey = useMemo(() => {
    if (!selectedFabricationOrder || !selectedJobReference) return null
    return selectedFabricationOrder.id + '-' + selectedJobReference.id
  }, [selectedFabricationOrder, selectedJobReference])

  return (
    <Box
      sx={{
        borderRight: { md: 1 },
        borderColor: { md: 'divider' },
        overflowY: 'auto',
        pr: { md: 1 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '&::-webkit-scrollbar': {
          width: 6
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'background.button',
          borderRadius: 3
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'background.buttonHover'
        },
        scrollbarWidth: 'thin',
        scrollbarColor: '#424242 transparent'
      }}
    >
      {items.map((item) => {
        const key = item.fabricationOrder.id + '-' + item.jobReference.id
        const percentage = progressByKey[key]
        const selected = key === selectedKey
        return (
          <ActiveJobListRow
            key={key}
            item={item}
            selected={selected}
            percentage={percentage}
            batchBadge={batchBadges[item.batchId] ?? null}
            onSelect={() => onSelectNode(item.jobReference.node, item.fabricationOrder, item.jobReference)}
          />
        )
      })}
    </Box>
  )
}

ActiveJobList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    fabricationOrder: PropTypes.object.isRequired,
    jobReference: PropTypes.object.isRequired
  })).isRequired,
  selectedFabricationOrder: PropTypes.object,
  selectedJobReference: PropTypes.object,
  progressByKey: PropTypes.object.isRequired,
  onSelectNode: PropTypes.func.isRequired,
  batchBadges: PropTypes.object
}

export default React.memo(ActiveJobList)
